import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';
import { Shield, UserPlus, CheckCircle, XCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const RemcachesPage = () => {
  const { t } = useLanguage();
  const [hasAdmin, setHasAdmin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [username, setUsername] = useState('');
  const [pin, setPin] = useState('');
  const [email, setEmail] = useState('');
  const [telephone, setTelephone] = useState('');
  const [creating, setCreating] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    checkAdmin();
  }, []);

  const checkAdmin = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/setup/check`);
      setHasAdmin(res.data.has_admin);
    } catch (err) {
      setError(t('remcaches.err_connexion'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!username.trim() || !pin.trim()) {
      setError(t('remcaches.err_champs_requis'));
      return;
    }
    if (pin.length < 4) {
      setError(t('remcaches.err_pin_court'));
      return;
    }

    setCreating(true);
    setError('');
    try {
      const res = await axios.post(`${API_URL}/api/setup/create-admin`, {
        username: username.trim(),
        password: pin.trim(),
        email: email.trim() || undefined,
        telephone: telephone.trim() || undefined
      });
      setResult(res.data);
      setHasAdmin(true);
    } catch (err) {
      setError(err.response?.data?.detail || t('remcaches.err_creation'));
    } finally {
      setCreating(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0a0e19]">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0a0e19] p-4">
      <div className="w-full max-w-md">
        <div className="rounded-2xl border border-white/10 bg-[#0f1320] p-8 shadow-2xl">
          <div className="flex justify-center mb-6">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-red-500 to-red-700 flex items-center justify-center">
              <Shield className="w-8 h-8 text-white" />
            </div>
          </div>

          <h1 className="text-2xl font-black text-center text-red-400 mb-1">R.E.Mobiles</h1>
          <p className="text-center text-sm text-gray-400 mb-6">{t('remcaches.config_initiale')}</p>

          {hasAdmin && !result ? (
            <div className="text-center">
              <XCircle className="w-12 h-12 text-red-400 mx-auto mb-3" />
              <p className="text-gray-300 font-semibold mb-1">{t('remcaches.deja_config')}</p>
              <p className="text-sm text-gray-500">{t('remcaches.admin_existe')}</p>
              <a href="/login" className="inline-block mt-4 px-6 py-2 rounded-xl bg-red-500/20 text-red-400 hover:bg-red-500/30 font-semibold text-sm">
                {t('remcaches.aller_connexion')}
              </a>
            </div>
          ) : result ? (
            <div className="text-center">
              <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-3" />
              <p className="text-green-400 font-bold mb-2">{t('remcaches.admin_cree')}</p>
              <div className="p-4 rounded-xl bg-black/30 border border-white/10 text-left text-sm space-y-1 mb-4">
                <p className="text-gray-300"><span className="text-gray-500">{t('remcaches.label_utilisateur')}</span> {username}</p>
                <p className="text-gray-300"><span className="text-gray-500">{t('remcaches.label_role')}</span> admin</p>
              </div>
              <p className="text-xs text-gray-500 mb-4">{t('remcaches.conserver_identifiants')}</p>
              <a href="/login" className="inline-block px-6 py-3 rounded-xl bg-gradient-to-r from-red-500 to-red-600 text-white font-bold hover:from-red-400 hover:to-red-500">
                {t('remcaches.se_connecter')}
              </a>
            </div>
          ) : (
            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <label className="block text-sm text-gray-300 mb-1">{t('remcaches.label_username')}</label>
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl bg-black/40 border border-white/10 text-white focus:border-red-500/50 focus:outline-none"
                  placeholder={t('remcaches.ph_username')}
                  data-testid="setup-username"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-300 mb-1">{t('remcaches.label_pin')}</label>
                <input
                  type="password"
                  inputMode="numeric"
                  value={pin}
                  onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
                  className="w-full px-4 py-3 rounded-xl bg-black/40 border border-white/10 text-white focus:border-red-500/50 focus:outline-none"
                  placeholder={t('remcaches.ph_pin')}
                  data-testid="setup-pin"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-300 mb-1">{t('remcaches.label_email')}</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl bg-black/40 border border-white/10 text-white focus:border-red-500/50 focus:outline-none"
                  placeholder="contact@remobiles.com"
                  data-testid="setup-email"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-300 mb-1">{t('remcaches.label_telephone')}</label>
                <input
                  type="text"
                  value={telephone}
                  onChange={(e) => setTelephone(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl bg-black/40 border border-white/10 text-white focus:border-red-500/50 focus:outline-none"
                  placeholder="+594 694 ..."
                  data-testid="setup-phone"
                />
              </div>

              {error && (
                <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/30 text-red-400 text-sm" data-testid="setup-error">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={creating}
                className="w-full py-3 rounded-xl bg-gradient-to-r from-red-500 to-red-600 text-white font-bold hover:from-red-400 hover:to-red-500 flex items-center justify-center gap-2"
                data-testid="setup-submit"
              >
                {creating ? (
                  <div className="loading-spinner w-5 h-5"></div>
                ) : (
                  <>
                    <UserPlus className="w-5 h-5" />
                    {t('remcaches.btn_creer_admin')}
                  </>
                )}
              </button>
            </form>
          )}
        </div>
        <p className="text-center text-xs text-gray-600 mt-4">{t('remcaches.footer')}</p>
      </div>
    </div>
  );
};

export default RemcachesPage;
